import { Module } from "vuex";
import { api } from "@/util/axios";

interface ReportData {
  report_name: string;
  report_type?: string;
  value: number;
  count?: number;
}

interface Client {
  _id: string;
  fullName: string;
  email: string;
  gender?: string;
  createdAt: string;
}

interface ReportsState {
  salesReports: ReportData[];
  clientReports: ReportData[];
  financialReports: ReportData[];
  employeeReports: ReportData[];
  paymentReports: ReportData[];
  upcomingReports: ReportData[];
  newClients: Client[];
  loading: boolean;
  error: string | null;
}

const reports: Module<ReportsState, unknown> = {
  namespaced: true,
  state: () => ({
    salesReports: [],
    clientReports: [],
    financialReports: [],
    employeeReports: [],
    paymentReports: [],
    upcomingReports: [],
    newClients: [],
    loading: false,
    error: null,
  }),
  getters: {
    salesReports: (state) => state.salesReports,
    clientReports: (state) => state.clientReports,
    financialReports: (state) => state.financialReports,
    employeeReports: (state) => state.employeeReports,
    paymentReports: (state) => state.paymentReports,
    upcomingReports: (state) => state.upcomingReports,
    newClients: (state) => state.newClients,
    loading: (state) => state.loading,
    error: (state) => state.error,
  },
  mutations: {
    SET_LOADING(state, loading: boolean) {
      state.loading = loading;
    },
    SET_ERROR(state, error: string | null) {
      state.error = error;
    },
    SET_SALES_REPORTS(state, reportsData: ReportData[]) {
      state.salesReports = reportsData;
    },
    SET_CLIENT_REPORTS(state, reportsData: ReportData[]) {
      state.clientReports = reportsData;
    },
    SET_FINANCIAL_REPORTS(state, reportsData: ReportData[]) {
      state.financialReports = reportsData;
    },
    SET_EMPLOYEE_REPORTS(state, reportsData: ReportData[]) {
      state.employeeReports = reportsData;
    },
    SET_PAYMENT_REPORTS(state, reportsData: ReportData[]) {
      state.paymentReports = reportsData;
    },
    SET_UPCOMING_REPORTS(state, reportsData: ReportData[]) {
      state.upcomingReports = reportsData;
    },
    SET_NEW_CLIENTS(state, clientsData: Client[]) {
      state.newClients = clientsData;
    },
  },
  actions: {
    async fetchSalesReports({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/sales-report?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Sales" },
        });
        const reportsData = response.data.reports.map((x: ReportData) => ({
          ...x,
          report_name: x.report_type || x.report_name,
        }));

        commit("SET_SALES_REPORTS", reportsData);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch sales reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchClientReports({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/client-report?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Client" },
        });
        commit("SET_CLIENT_REPORTS", response.data.reports);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch client reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchFinancialReports({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/financial-report?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Finance" },
        });
        const reportsData = response.data.reports.map((x: ReportData) => ({
          ...x,
          report_name: x.report_type || x.report_name,
        }));

        commit("SET_FINANCIAL_REPORTS", reportsData);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch financial reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchEmployeeReports({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/employee-report?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Staff" },
        });
        commit("SET_EMPLOYEE_REPORTS", response.data.reports);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch employee reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchPaymentReports({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/payments?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Payment" },
        });

        // Transform the API response to match the expected ReportData interface
        const transformedReports = response.data.reports.map(
          (report: {
            _id: string | null;
            totalAmount: number;
            count: number;
          }) => ({
            report_name: report._id || "Unknown",
            value: report.totalAmount || 0,
            count: report.count || 0,
          })
        );

        commit("SET_PAYMENT_REPORTS", transformedReports);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch payment reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchUpcomingReports({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/staff-revenue?businessId=${businessId}&upcoming=yes`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Projected Revenue" },
        });
        commit("SET_UPCOMING_REPORTS", response.data.reports);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch upcoming reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchUpcomingReportsWithEmployee(
      { commit, rootGetters },
      params = {}
    ) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/staff-revenue?businessId=${businessId}&upcoming=yes`;

        if (params.employee?.eId) {
          apiUrl += `&staff=${params.employee.eId}`;
        }

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Projected Revenue" },
        });
        commit("SET_UPCOMING_REPORTS", response.data.reports);
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch upcoming reports with employee";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchTopFilterStats(
      { commit, rootGetters },
      params: {
        reportType?: string;
        method?: string;
        staff?: string;
        startDate?: string;
        endDate?: string;
      }
    ) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        const { reportType, method, staff, startDate, endDate } = params;

        // Helper function for sales report data
        const fetchSalesReportForType = async (
          type: string,
          startDate?: string,
          endDate?: string
        ) => {
          let apiUrl = `/v1/sales-ledger/sales-report?businessId=${businessId}`;

          if (startDate) {
            const date = new Date(startDate);
            date.setHours(0, 0, 0, 0);
            apiUrl += `&startDate=${date.toISOString()}`;
          }
          if (endDate) {
            const date = new Date(endDate);
            date.setHours(23, 59, 59, 59);
            apiUrl += `&endDate=${date.toISOString()}`;
          }

          const response = await api.get(apiUrl, {
            headers: { "vendor-module": "reports.Sales" },
          });
          const stats = response.data.reports
            .map((x: ReportData) => ({
              ...x,
              report_name: x.report_type || x.report_name,
            }))
            .find((x: ReportData) => x.report_name === type);

          return stats ? stats.value : 0;
        };

        // Helper function for staff revenue
        const fetchStaffRevenueForTopFilter = async (
          startDate?: string,
          endDate?: string
        ) => {
          let apiUrl = `/v1/sales-ledger/staff-revenue?businessId=${businessId}&paymentStatus=completed&type=service`;

          if (startDate) {
            const date = new Date(startDate);
            date.setHours(0, 0, 0, 0);
            apiUrl += `&startDate=${date.toISOString()}`;
          }
          if (endDate) {
            const date = new Date(endDate);
            date.setHours(23, 59, 59, 59);
            apiUrl += `&endDate=${date.toISOString()}`;
          }

          const response = await api.get(apiUrl, {
            headers: { "vendor-module": "reports.Projected Revenue" },
          });
          return response.data.reports || 0;
        };

        // Helper function for payments
        const fetchPaymentsForTopFilter = async (
          method: string,
          startDate?: string,
          endDate?: string
        ) => {
          let apiUrl = `/v1/sales-ledger/payments?businessId=${businessId}`;

          if (startDate) {
            const date = new Date(startDate);
            date.setHours(0, 0, 0, 0);
            apiUrl += `&startDate=${date.toISOString()}`;
          }
          if (endDate) {
            const date = new Date(endDate);
            date.setHours(23, 59, 59, 59);
            apiUrl += `&endDate=${date.toISOString()}`;
          }

          const response = await api.get(apiUrl, {
            headers: { "vendor-module": "reports.Payment" },
          });
          const stats = response.data.reports
            .map((x: { _id: string; totalAmount: number }) => ({
              ...x,
              report_name: x._id,
              value: x.totalAmount,
            }))
            .find(
              (x: { _id: string; totalAmount: number }) => x._id === method
            );

          return stats ? stats.totalAmount : 0;
        };

        let stats = 0;
        let statsTitle = "";

        if (reportType === "all sale") {
          statsTitle = "All Sales";
          stats = await fetchSalesReportForType(
            "All Sales",
            startDate,
            endDate
          );
        } else if (reportType === "product") {
          statsTitle = "Product Sales";
          stats = await fetchSalesReportForType("Products", startDate, endDate);
        } else if (reportType === "service") {
          statsTitle = "Services Sales";
          stats = await fetchSalesReportForType("Services", startDate, endDate);
        } else if (reportType === "service package") {
          statsTitle = "Service package Sales";
          stats = await fetchSalesReportForType(
            "Service Packages",
            startDate,
            endDate
          );
        } else if (reportType === "membership") {
          statsTitle = "Membership Sales";
          stats = await fetchSalesReportForType(
            "Memberships",
            startDate,
            endDate
          );
        } else if (reportType === "voucher") {
          statsTitle = "Voucher Sales";
          stats = await fetchSalesReportForType("Vouchers", startDate, endDate);
        } else if (reportType === "total deposit") {
          statsTitle = "Total Deposits";
          stats = await fetchSalesReportForType(
            "Total Deposits",
            startDate,
            endDate
          );
        }

        if (staff === "service&paymentStatus=completed&type=service") {
          statsTitle = "Staff Revenue";
          stats = await fetchStaffRevenueForTopFilter(startDate, endDate);
        }

        if (method) {
          statsTitle = `${method} payments`;
          stats = await fetchPaymentsForTopFilter(method, startDate, endDate);
        }

        return { stats, statsTitle };
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch top filter stats";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchClientStats(
      { commit, rootGetters },
      params: {
        reportType: string;
        startDate?: string;
        endDate?: string;
      }
    ) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/sales-ledger/client-report?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }

        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Client" },
        });
        const report = response.data.reports.find((r: ReportData) =>
          r.report_name.toLowerCase().includes(params.reportType)
        );

        return report ? report.value : 0;
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch client stats";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchDetailedEmployeeReports(
      { commit, rootGetters },
      params: {
        status?: string;
        paymentStatus?: string;
        startDate?: string;
        endDate?: string;
        upcoming?: boolean;
      }
    ) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }

        let apiUrl = `/v1/appointments/?businessId=${businessId}`;

        if (params.status) {
          apiUrl += `&status=${params.status}`;
        }
        if (params.paymentStatus) {
          apiUrl += `&paymentStatus=${params.paymentStatus}`;
        }
        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }
        if (params.upcoming) {
          apiUrl += `&upcoming=yes`;
        }

        const response = await api.get(apiUrl);
        return response.data.reports;
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch detailed employee reports";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchNewClients({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        const role = rootGetters.role;
        if (!role) {
          throw new Error("Role not found");
        }

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }
        let apiUrl = `/v1/sales-ledger/new-clients?businessId=${businessId}`;

        if (params.startDate) {
          apiUrl += `&startDate=${params.startDate}`;
        }
        if (params.endDate) {
          apiUrl += `&endDate=${params.endDate}`;
        }
        if (params.fetchAll) {
          apiUrl += `&fetchAll=${params.fetchAll}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Client" },
        });
        commit("SET_NEW_CLIENTS", response.data.newClients);
        return response.data.newClients;
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch new clients";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchStaffRevenue({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        const role = rootGetters.role;
        if (!role) {
          throw new Error("Role not found");
        }

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }
        let apiUrl = `/v1/sales-ledger/staff-revenue?businessId=${businessId}`;

        if (params.startDate) {
          const date = new Date(params.startDate);
          date.setHours(0, 0, 0, 0);
          apiUrl += `&startDate=${date.toISOString()}`;
        }
        if (params.endDate) {
          const date = new Date(params.endDate);
          date.setHours(23, 59, 59, 59);
          apiUrl += `&endDate=${date.toISOString()}`;
        }
        if (params.employeeId) {
          apiUrl += `&staff=${params.employeeId}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Projected Revenue" },
        });
        commit("SET_EMPLOYEE_REPORTS", response.data.reports || []);
        return response.data.reports || [];
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to fetch staff revenue";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchPayments({ commit, rootGetters }, params = {}) {
      try {
        commit("SET_LOADING", true);
        const role = rootGetters.role;
        if (!role) {
          throw new Error("Role not found");
        }

        const businessId = rootGetters.role?.business?._id;
        if (!businessId) {
          throw new Error("Business ID not found");
        }
        let apiUrl = `/v1/sales-ledger/payments?businessId=${businessId}`;

        if (params.startDate) {
          apiUrl += `&startDate=${params.startDate}`;
        }
        if (params.endDate) {
          apiUrl += `&endDate=${params.endDate}`;
        }

        const response = await api.get(apiUrl, {
          headers: { "vendor-module": "reports.Payment" },
        });
        commit("SET_PAYMENT_REPORTS", response.data);
        return response.data;
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Failed to fetch payments";
        commit("SET_ERROR", errorMsg);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
  },
};

export default reports;
